import React, { useState } from 'react'
import { Linking, Platform, StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { ContextualDropdown, DeleteModal, Icon, Text } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import type { EventAttendee } from '@hooks'
import { EventAvatar } from './EventAvatar'

interface AttendeeCardProps {
  attendee: EventAttendee
  onAdd: (attendee: EventAttendee) => void
  onRemove: (attendee: EventAttendee) => void
  isAdding: boolean
}

/** Full profile (from the registration on): who they are, and how to add them. */
export function AttendeeCard({ attendee, onAdd, onRemove, isAdding }: AttendeeCardProps) {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const role = [attendee.jobTitle, attendee.company].filter(Boolean).join(' · ')

  return (
    <View style={styles.card}>
      <EventAvatar
        firstName={attendee.firstName}
        lastName={attendee.lastName}
        avatarUrl={attendee.avatarUrl}
        size={52}
      />
      <View style={styles.info}>
        <Text type="bodyBold" numberOfLines={1}>
          {attendee.isSelf
            ? `${attendee.firstName} ${attendee.lastName ?? ''} (${t('events.public.you')})`
            : `${attendee.firstName} ${attendee.lastName ?? ''}`}
        </Text>
        {role ? (
          <Text type="bodySmall" color={theme.colors.greyBlue} numberOfLines={2}>
            {role}
          </Text>
        ) : null}
        <View style={styles.actions}>
          <RelationAction attendee={attendee} onAdd={onAdd} onRemove={onRemove} isAdding={isAdding} />
          {attendee.linkedinUrl ? (
            <TouchableOpacity
              accessibilityRole="link"
              style={styles.linkedin}
              onPress={() => Linking.openURL(attendee.linkedinUrl as string)}
            >
              <Icon name="linkedin" size={16} color={theme.colors.linkedin} />
              <Text type="bodySmallBold" color={theme.colors.linkedin} style={styles.linkedinText}>
                {t('events.relation.linkedin')}
              </Text>
            </TouchableOpacity>
          ) : null}
        </View>
      </View>
    </View>
  )
}

function RelationAction({ attendee, onAdd, onRemove, isAdding }: AttendeeCardProps) {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const [confirmRemove, setConfirmRemove] = useState(false)

  if (attendee.isSelf || attendee.relation === 'self') return null
  if (attendee.relation === 'contact') {
    return (
      <>
        <View testID="in-contacts">
          <ContextualDropdown
            data={[{ id: 'remove', name: t('events.relation.remove') }]}
            label={t('events.relation.contact')}
            onSelect={() => setConfirmRemove(true)}
            buttonStyle={styles.contactMenu}
          />
        </View>
        <DeleteModal
          isVisible={confirmRemove}
          setIsVisible={setConfirmRemove}
          title={t('events.relation.removeTitle', { name: attendee.firstName })}
          description={t('events.relation.removeDescription')}
          deleteLabel={t('events.relation.remove')}
          onDelete={() => {
            setConfirmRemove(false)
            onRemove(attendee)
          }}
        />
      </>
    )
  }
  return (
    <TouchableOpacity
      accessibilityRole="button"
      disabled={isAdding}
      style={[styles.addButton, isAdding && styles.addButtonBusy]}
      onPress={() => onAdd(attendee)}
    >
      <Icon name="userPlus" size={16} color={theme.colors.white} />
      <Text type="bodySmallBold" color={theme.colors.white} style={styles.linkedinText}>
        {t('events.relation.add')}
      </Text>
    </TouchableOpacity>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    card: {
      flexDirection: 'row',
      padding: theme.spacing.lg,
      borderRadius: theme.border.large,
      backgroundColor: theme.colors.white,
      borderWidth: 1,
      borderColor: theme.colors.grey,
      gap: theme.spacing.md,
      ...(Platform.OS === 'web' ? ({ transition: 'box-shadow 160ms ease' } as object) : {}),
    },
    info: { flex: 1, minWidth: 0, gap: 2 },
    actions: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: theme.spacing.sm,
      marginTop: theme.spacing.sm,
    },
    addButton: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: theme.colors.primary,
      borderRadius: theme.border.jumbo,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.xs + 2,
    },
    addButtonBusy: { opacity: 0.6 },
    contactMenu: { flex: 0, paddingVertical: theme.spacing.xs + 2, paddingHorizontal: theme.spacing.md },
    linkedin: {
      flexDirection: 'row',
      alignItems: 'center',
      borderRadius: theme.border.jumbo,
      borderWidth: 1,
      borderColor: theme.colors.linkedin,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.xs + 1,
    },
    linkedinText: { marginLeft: theme.spacing.xs },
  })
